/**
 * Profile Image Service
 * 
 * Service for managing profile images using Supabase Storage
 */

import { supabase } from '../lib/supabase';
import { getCurrentUserId } from '../lib/supabase';
import type {
  ImageUploadResponse,
  SuccessResponse
} from '../types/profile.types';

const BUCKET_NAME = 'profile-images';
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'];

export interface ProfileImageServiceResponse<T> {
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
}

/**
 * Validate image file
 */
function validateImageFile(file: File): { valid: boolean; error?: string } {
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `File size must be less than 5MB. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`
    };
  }

  // Check MIME type
  if (!ALLOWED_MIME_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: `File format not supported. Allowed formats: JPG, PNG, WebP`
    };
  }

  // Check file extension
  const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
  if (!ALLOWED_EXTENSIONS.includes(extension)) {
    return {
      valid: false,
      error: `File extension not supported. Allowed extensions: .jpg, .jpeg, .png, .webp`
    };
  }

  return { valid: true };
}

/**
 * Generate file path for profile image
 */
function generateImagePath(userId: string, filename: string): string {
  const timestamp = Date.now();
  const sanitizedFilename = filename.replace(/[^a-zA-Z0-9.-]/g, '_');
  return `${userId}/${timestamp}-${sanitizedFilename}`;
}

export const profileImageService = {
  /**
   * Upload profile image
   */
  async uploadImage(file: File): Promise<ImageUploadResponse> {
    try {
      console.log('ProfileImageService: Starting upload for file:', file.name);
      const userId = await getCurrentUserId();
      
      if (!userId) {
        console.error('ProfileImageService: No user ID found');
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'User is not authenticated'
          }
        };
      }
      
      console.log('ProfileImageService: User ID:', userId);

      // Validate file
      const validation = validateImageFile(file);
      if (!validation.valid) {
        return {
          error: {
            code: 'VALIDATION_ERROR',
            message: validation.error || 'Invalid file'
          }
        };
      }

      // Check if user has existing image and delete it
      const existingImages = await supabase.storage
        .from(BUCKET_NAME)
        .list(userId);

      if (existingImages.error) {
        console.warn('Error listing existing images (may be first upload):', existingImages.error);
        // Continue anyway - this might be the first upload
      }

      if (existingImages.data && existingImages.data.length > 0) {
        const filesToDelete = existingImages.data.map(img => `${userId}/${img.name}`);
        const deleteResult = await supabase.storage
          .from(BUCKET_NAME)
          .remove(filesToDelete);
        
        if (deleteResult.error) {
          console.warn('Error deleting existing images:', deleteResult.error);
          // Continue anyway - we'll upload the new one
        }
      }

      // Generate file path
      const filePath = generateImagePath(userId, file.name);
      console.log('ProfileImageService: Uploading to path:', filePath);

      // Upload image
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from(BUCKET_NAME)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });
      
      console.log('ProfileImageService: Upload result:', { 
        data: uploadData, 
        error: uploadError 
      });

      if (uploadError) {
        console.error('Supabase storage upload error:', uploadError);
        return {
          error: {
            code: (uploadError as any).statusCode === 403 ? 'PERMISSION_ERROR' : 'UPLOAD_ERROR',
            message: uploadError.message || 'Failed to upload image',
            details: uploadError
          }
        };
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from(BUCKET_NAME)
        .getPublicUrl(filePath);

      const imageUrl = urlData.publicUrl;

      // Update profile_image_url in user_profiles
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ profile_image_url: imageUrl })
        .eq('user_id', userId);

      if (updateError) {
        // If update fails, try to clean up uploaded file
        await supabase.storage
          .from(BUCKET_NAME)
          .remove([filePath]);

        return {
          error: {
            code: 'UNKNOWN_ERROR',
            message: updateError.message || 'Failed to update profile image URL'
          }
        };
      }

      return {
        data: {
          url: imageUrl,
          path: filePath
        }
      };
    } catch (error: any) {
      console.error('Error uploading image:', error);
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error.message || 'Failed to upload image'
        }
      };
    }
  },

  /**
   * Delete profile image
   */
  async deleteImage(): Promise<SuccessResponse> {
    try {
      const userId = await getCurrentUserId();
      
      if (!userId) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'User is not authenticated'
          }
        };
      }

      // List user's images
      const { data: images, error: listError } = await supabase.storage
        .from(BUCKET_NAME)
        .list(userId);

      if (listError) {
        return {
          error: {
            code: 'UNKNOWN_ERROR',
            message: listError.message || 'Failed to list images'
          }
        };
      }

      // If no images, still return success
      if (!images || images.length === 0) {
        // Update profile_image_url to null
        await supabase
          .from('user_profiles')
          .update({ profile_image_url: null })
          .eq('user_id', userId);

        return {
          data: {
            success: true
          }
        };
      }

      // Delete all user's images
      const filesToDelete = images.map(img => `${userId}/${img.name}`);
      const { error: deleteError } = await supabase.storage
        .from(BUCKET_NAME)
        .remove(filesToDelete);

      if (deleteError) {
        return {
          error: {
            code: 'UNKNOWN_ERROR',
            message: deleteError.message || 'Failed to delete image'
          }
        };
      }

      // Update profile_image_url to null
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ profile_image_url: null })
        .eq('user_id', userId);

      if (updateError) {
        return {
          error: {
            code: 'UNKNOWN_ERROR',
            message: updateError.message || 'Failed to update profile image URL'
          }
        };
      }

      return {
        data: {
          success: true
        }
      };
    } catch (error: any) {
      console.error('Error deleting image:', error);
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error.message || 'Failed to delete image'
        }
      };
    }
  }
};

